import React from "react";
import {
  Accordion,
  Alert,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  TextField,
  Box,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import { StatusChip } from "../../components/StatusChip";

const entraUserProfileUrl = (userId: string) =>
  "https://portal.azure.com/#view/Microsoft_AAD_UsersAndTenants/UserProfileMenuBlade/~/overview/userId/" +
  userId;

interface Props {
  formData: any;
  isAdmin: boolean;
  createNewEvent: boolean;
  eventApproved: boolean;
  formattedDate: string;
  formatDateForInput: (v: string) => string;
  handleRisingEventChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const EventVersionInfo: React.FC<Props> = ({
  formData,
  isAdmin,
  createNewEvent,
  eventApproved,
  formattedDate,
  formatDateForInput,
  handleRisingEventChange,
}) => {
  if (createNewEvent) return null;
  const attrs = (formData?.attributes ?? {}) as Record<string, unknown>;
  const reassignmentHistory = Array.isArray(attrs.event_code_reassignment_history)
    ? (attrs.event_code_reassignment_history as Array<Record<string, unknown>>)
    : [];
  const previousCodes = Array.from(
    new Set(
      reassignmentHistory
        .map((entry) => Number(entry.old_event_code))
        .filter((code) => Number.isFinite(code) && code !== Number(formData.event_code))
    )
  ) as number[];
  const contractRenameHistory = Array.isArray(attrs.contract_rename_history)
    ? (attrs.contract_rename_history as Array<Record<string, unknown>>)
    : [];
  const contractRenameRows = contractRenameHistory.map((entry, index) => {
    const fromName = (entry.from ?? "").toString();
    const toName = (entry.to ?? "").toString();
    const changedAtRaw = (entry.at ?? "").toString();
    const changedAt = changedAtRaw ? formatDateForInput(changedAtRaw) : "—";
    return {
      key: `cr-${changedAtRaw}-${index}`,
      fromName: fromName || "—",
      toName: toName || "—",
      changedAt,
    };
  });
  const historyRows = reassignmentHistory.map((entry, index) => {
    const oldCode = Number(entry.old_event_code);
    const newCode = Number(entry.new_event_code);
    const oldVersion = Number(entry.old_version);
    const newVersion = Number(entry.new_version);
    const changedAtRaw = (entry.changed_at ?? "").toString();
    const changedAt = changedAtRaw ? formatDateForInput(changedAtRaw) : "—";
    return {
      key: `${changedAtRaw}-${index}`,
      oldCode: Number.isFinite(oldCode) ? oldCode : null,
      newCode: Number.isFinite(newCode) ? newCode : null,
      oldVersion: Number.isFinite(oldVersion) ? oldVersion : null,
      newVersion: Number.isFinite(newVersion) ? newVersion : null,
      changedAt,
    };
  });

  const creatorUuid = (formData.created_by ?? "").toString().trim();
  const creatorName = (formData.created_by_display_name ?? "").toString().trim();
  const creatorShown = creatorName || creatorUuid || "—";
  const creatorHelper =
    creatorName && creatorUuid ? `Object ID: ${creatorUuid}` : undefined;

  const approverUuid = (formData.approved_by ?? "").toString().trim();
  const approverName = (formData.approved_by_display_name ?? "").toString().trim();
  const hasApproval = Boolean(
    formData.approved_at && String(formData.approved_at).trim() !== ""
  );
  const approverShown = hasApproval
    ? approverName || approverUuid || "—"
    : "";
  const approverHelper =
    hasApproval && approverName && approverUuid
      ? `Object ID: ${approverUuid}`
      : undefined;

  const showCreatorEntraButton = Boolean(creatorUuid && !creatorName);
  const showApproverEntraButton = Boolean(hasApproval && approverUuid && !approverName);

  return (
    <Box sx={{ mt: 3, width: "100%" }}>
    <Accordion
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          Version information
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", ml: 3 }}>
          <StatusChip approved={eventApproved} />
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container spacing={2}>
          {previousCodes.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                This event had a different event code before: {previousCodes.join(", ")}.
              </Alert>
            </Grid>
          )}
          {historyRows.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Event code reassignment history
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Changed at</TableCell>
                      <TableCell>Code change</TableCell>
                      <TableCell>Version change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyRows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>{row.changedAt}</TableCell>
                        <TableCell>
                          {row.oldCode ?? "—"} {"->"} {row.newCode ?? "—"}
                        </TableCell>
                        <TableCell>
                          v{row.oldVersion ?? "—"} {"->"} v{row.newVersion ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
          {contractRenameRows.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Contract rename history
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Changed at</TableCell>
                      <TableCell>Name change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contractRenameRows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>{row.changedAt}</TableCell>
                        <TableCell>
                          {row.fromName} {"->"} {row.toName}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Version"
              name="version"
              value={formData.version ?? ""}
              slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
              fullWidth
              disabled
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Created at"
              name="create_date"
              slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
              value={
                formData.created_at && formData.created_at.trim() !== ""
                  ? formatDateForInput(formData.created_at)
                  : formattedDate
              }
              onChange={handleRisingEventChange}
              fullWidth
              disabled
            />
          </Grid>

          {isAdmin &&
            (creatorName ? (
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  label="Created by"
                  name="created_by"
                  slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
                  value={creatorShown}
                  helperText={creatorHelper}
                  fullWidth
                  disabled
                />
              </Grid>
            ) : (
              <>
                <Grid size={{ xs: 12, sm: showCreatorEntraButton ? 6 : 8 }}>
                  <TextField
                    label="Created by"
                    name="created_by"
                    slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
                    value={creatorUuid || "—"}
                    fullWidth
                    disabled
                  />
                </Grid>
                {showCreatorEntraButton && (
                  <Grid
                    size={{ xs: 12, sm: 2 }}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <Button
                      variant="outlined"
                      color="secondary"
                      component={Link}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<PersonIcon />}
                      href={entraUserProfileUrl(creatorUuid)}
                    >
                      Entra
                    </Button>
                  </Grid>
                )}
              </>
            ))}

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Approved at"
              slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
              value={
                formData.approved_at && formData.approved_at.trim() !== ""
                  ? formatDateForInput(formData.approved_at)
                  : ""
              }
              fullWidth
              disabled
            />
          </Grid>

          {isAdmin &&
            (approverName ? (
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  label="Approved by"
                  slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
                  value={approverShown}
                  helperText={approverHelper}
                  fullWidth
                  disabled
                />
              </Grid>
            ) : (
              <>
                <Grid size={{ xs: 12, sm: showApproverEntraButton ? 6 : 8 }}>
                  <TextField
                    label="Approved by"
                    slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
                    value={hasApproval ? approverUuid || "—" : ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                {showApproverEntraButton && (
                  <Grid
                    size={{ xs: 12, sm: 2 }}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <Button
                      variant="outlined"
                      color="secondary"
                      component={Link}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<PersonIcon />}
                      href={entraUserProfileUrl(approverUuid)}
                    >
                      Entra
                    </Button>
                  </Grid>
                )}
              </>
            ))}
        </Grid>
      </AccordionDetails>
    </Accordion>
    </Box>
  );
};
